
import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrashIcon } from 'lucide-react';
import { toast } from 'sonner';

interface ApiKey {
  id: string;
  key_name: string;
  api_key: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  rate_limit_per_hour: number;
  usage_count: number;
}

interface ApiKeysTableProps {
  apiKeys: ApiKey[];
  onDelete: (id: string) => Promise<void>;
  onToggleStatus: (id: string, currentStatus: boolean) => Promise<void>;
}

const ApiKeysTable = ({ apiKeys, onDelete, onToggleStatus }: ApiKeysTableProps) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('API key copied to clipboard');
  };

  if (apiKeys.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        No API keys created yet. Create your first API key to get started.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>API Key</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Rate Limit</TableHead>
          <TableHead>Usage</TableHead>
          <TableHead>Last Used</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apiKeys.map((key) => (
          <TableRow key={key.id}>
            <TableCell className="font-medium">{key.key_name}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  {key.api_key.substring(0, 20)}...
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(key.api_key)}
                >
                  Copy
                </Button>
              </div>
            </TableCell>
            <TableCell>
              <Button
                variant={key.is_active ? "outline" : "secondary"}
                size="sm"
                onClick={() => onToggleStatus(key.id, key.is_active)}
              >
                {key.is_active ? 'Active' : 'Inactive'}
              </Button>
            </TableCell>
            <TableCell>{key.rate_limit_per_hour}/hour</TableCell>
            <TableCell>{key.usage_count}</TableCell>
            <TableCell>
              {key.last_used_at 
                ? new Date(key.last_used_at).toLocaleDateString()
                : 'Never'
              }
            </TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(key.id)}
                className="text-destructive hover:text-destructive"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ApiKeysTable;
